import { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Pencil, Trash2, RefreshCw, ExternalLink } from 'lucide-react';
import { SortableTable, type SortableColumn } from '../ui/SortableTable';
import { adminListUsers, adminUpsertUser, adminDeleteUser } from '../../utils/database';
import { trackerListUsers, trackerUpsertUser } from '../../utils/tracker';
import type { AdminUserRecord } from '../../utils/database';
import type { TrackerUser } from '../../utils/tracker';
const ACCESS_LEVELS: AdobeImsAccessLevel[] = ['unregistered', 'viewer', 'editor', 'admin', 'superadmin'];
const TRACKER_ACCESS_LEVELS = ['viewer', 'editor', 'admin', 'superadmin'];
const ACCESS_LEVEL_COLORS: Record<string, string> = {
  unregistered: 'bg-slate-100 text-slate-500',
  viewer: 'bg-slate-100 text-slate-700',
  editor: 'bg-blue-100 text-blue-700',
  admin: 'bg-purple-100 text-purple-700',
  superadmin: 'bg-amber-100 text-amber-800'
};
function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}
export function UsersTab() {
  const [users, setUsers] = useState<AdminUserRecord[]>([]);
  const [trackerUsers, setTrackerUsers] = useState<TrackerUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [trackerLoading, setTrackerLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<AdminUserRecord | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    accessLevel: 'viewer' as AdobeImsAccessLevel
  });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // Tracker add/edit modal
  const [trackerModalUser, setTrackerModalUser] = useState<AdminUserRecord | null>(null);
  const [trackerAccessLevel, setTrackerAccessLevel] = useState('editor');
  const [trackerSaving, setTrackerSaving] = useState(false);
  const [trackerError, setTrackerError] = useState<string | null>(null);

  // Filters
  const [filterSearch, setFilterSearch] = useState('');
  const [filterAccessLevel, setFilterAccessLevel] = useState('');

  // Build tracker user map for O(1) lookup
  const trackerUserMap = useMemo(() => {
    const map = new Map<string, TrackerUser>();
    for (const u of trackerUsers) {
      map.set(u._id.toLowerCase(), u);
    }
    return map;
  }, [trackerUsers]);
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminListUsers();
      setUsers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);
  const loadTrackerUsers = useCallback(async () => {
    setTrackerLoading(true);
    try {
      const data = await trackerListUsers();
      setTrackerUsers(data);
    } catch (err) {
      console.error('Failed to load tracker users:', err);
      // Don't set main error — tracker is supplementary
    } finally {
      setTrackerLoading(false);
    }
  }, []);
  useEffect(() => {
    void loadUsers();
    void loadTrackerUsers();
  }, [loadUsers, loadTrackerUsers]);
  const filteredUsers = useMemo(() => {
    let result = users;
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      result = result.filter(u => u.email.toLowerCase().includes(q) || u.name && u.name.toLowerCase().includes(q));
    }
    if (filterAccessLevel) {
      result = result.filter(u => u.accessLevel === filterAccessLevel);
    }
    return result;
  }, [users, filterSearch, filterAccessLevel]);
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await adminUpsertUser({
        email: formData.email,
        name: formData.name || undefined,
        accessLevel: formData.accessLevel
      });
      setShowAddForm(false);
      setEditingUser(null);
      setFormData({
        email: '',
        name: '',
        accessLevel: 'viewer'
      });
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };
  const handleDelete = async (email: string) => {
    setSaving(true);
    setError(null);
    try {
      await adminDeleteUser(email);
      setConfirmDelete(null);
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setSaving(false);
    }
  };
  const startEdit = (user: AdminUserRecord) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name || '',
      accessLevel: user.accessLevel
    });
    setShowAddForm(true);
  };
  const openTrackerModal = (user: AdminUserRecord) => {
    const existing = trackerUserMap.get(user.email.toLowerCase());
    setTrackerModalUser(user);
    setTrackerAccessLevel(existing?.accessLevel || 'editor');
    setTrackerError(null);
  };
  const handleTrackerSave = async () => {
    if (!trackerModalUser) return;
    setTrackerSaving(true);
    setTrackerError(null);
    try {
      await trackerUpsertUser({
        email: trackerModalUser.email,
        name: trackerModalUser.name || undefined,
        accessLevel: trackerAccessLevel
      });
      setTrackerModalUser(null);
      await loadTrackerUsers();
    } catch (err) {
      setTrackerError(err instanceof Error ? err.message : 'Failed to add user to tracker');
    } finally {
      setTrackerSaving(false);
    }
  };
  const TRACKER_LEVEL_COLORS: Record<string, string> = {
    viewer: 'bg-slate-100 text-slate-700',
    editor: 'bg-blue-100 text-blue-700',
    admin: 'bg-purple-100 text-purple-700',
    superadmin: 'bg-amber-100 text-amber-800'
  };
  const columns: SortableColumn<AdminUserRecord>[] = [{
    key: 'email',
    label: 'Email',
    width: '22%',
    render: row => <span className="font-medium text-slate-900" data-id="element-604" data-mp="ydwnmyup2qax">{row.email}</span>,
    sortValue: row => row.email
  }, {
    key: 'name',
    label: 'Name',
    width: '15%',
    render: row => <span className="text-slate-600" data-id="element-605" data-mp="17el37xzqkypx">{row.name || '—'}</span>,
    sortValue: row => row.name || ''
  }, {
    key: 'accessLevel',
    label: 'Access Level',
    width: '11%',
    render: row => <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${ACCESS_LEVEL_COLORS[row.accessLevel] || 'bg-slate-100 text-slate-700'}`} data-id="element-606" data-mp="1haelly04zmfm">
          {row.accessLevel}
        </span>,
    sortValue: row => row.accessLevel
  }, {
    key: 'tracker',
    label: 'Tracker',
    width: '13%',
    render: row => {
      const trackerUser = trackerUserMap.get(row.email.toLowerCase());
      if (trackerLoading) {
        return <span className="text-xs text-slate-400" data-id="element-607" data-mp="3kkrhiqv5k8ma">Loading...</span>;
      }
      if (trackerUser) {
        return <div className="flex items-center gap-1.5" data-id="element-608" data-mp="3fkdj80jcsd1g">
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${TRACKER_LEVEL_COLORS[trackerUser.accessLevel] || 'bg-slate-100 text-slate-700'}`} data-id="element-609" data-mp="3bja27apkkayn">
                {trackerUser.accessLevel}
              </span>
              <button onClick={e => {
            e.stopPropagation();
            openTrackerModal(row);
          }} className="p-0.5 rounded hover:bg-slate-100 text-slate-400 hover:text-indigo-600 transition-colors" title="Edit tracker access" data-id="element-610" data-mp="741x3yggzazt">
                <Pencil size={11} data-id="element-611" data-mp="3pk4c5xs9mjzf" />
              </button>
            </div>;
      }
      return <button onClick={e => {
        e.stopPropagation();
        openTrackerModal(row);
      }} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors" data-id="element-612" data-mp="qudwg5barqr9">
            <Plus size={11} data-id="element-613" data-mp="3vbnm0njrp2i9" />
            Add
          </button>;
    },
    sortValue: row => {
      const t = trackerUserMap.get(row.email.toLowerCase());
      return t ? t.accessLevel : 'zzz'; // sort non-tracker users to bottom
    }
  }, {
    key: 'createdAt',
    label: 'Created',
    width: '14%',
    render: row => <span className="text-slate-500 text-xs" data-id="element-614" data-mp="d6bwmpvcmiwm">{formatDate(row.createdAt)}</span>,
    sortValue: row => row.createdAt || ''
  }, {
    key: 'lastLoginAt',
    label: 'Last Login',
    width: '14%',
    render: row => <span className="text-slate-500 text-xs" data-id="element-615" data-mp="86fs8piqopgj">{formatDate(row.lastLoginAt)}</span>,
    sortValue: row => row.lastLoginAt || ''
  }, {
    key: 'actions',
    label: '',
    width: '11%',
    render: row => <div className="flex items-center gap-1" data-id="element-616" data-mp="2swx3ugos0f13">
          <button onClick={e => {
        e.stopPropagation();
        startEdit(row);
      }} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-indigo-600 transition-colors" title="Edit user" data-id="element-617" data-mp="2sukdpg8v6ycy">
            <Pencil size={14} data-id="element-618" data-mp="2vbv7wwbcog9a" />
          </button>
          <button onClick={e => {
        e.stopPropagation();
        setConfirmDelete(row.email);
      }} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-red-600 transition-colors" title="Delete user" data-id="element-619" data-mp="2vak2ks29b0g2">
            <Trash2 size={14} data-id="element-620" data-mp="1xocq6vzpo3t5" />
          </button>
        </div>
  }];
  return <div className="space-y-4" data-id="element-621" data-mp="2cczgttusghzo">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-622" data-mp="2cekalh9a3wwh">
        <div data-id="element-623" data-mp="27kpu0moyzy1m">
          <h3 className="text-base font-semibold text-slate-900" data-id="element-624" data-mp="27kzbflvy8wvp">Users</h3>
          <p className="text-sm text-slate-500 mt-0.5" data-id="element-625" data-mp="1jvdbuxbo0fn6">
            Manage user accounts and access levels ({users.length} users)
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-626" data-mp="1ev953z0k6za5">
          <button onClick={() => {
          void loadUsers();
          void loadTrackerUsers();
        }} disabled={loading} className="p-2 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50" title="Refresh" data-id="element-627" data-mp="3jjbsx7wz4zkr">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} data-id="element-628" data-mp="35jiueqf7hf28" />
          </button>
          <button onClick={() => {
          setEditingUser(null);
          setFormData({
            email: '',
            name: '',
            accessLevel: 'viewer'
          });
          setShowAddForm(true);
        }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors" data-id="element-629" data-mp="30lr1bt2trmwg">
            <Plus size={14} data-id="element-630" data-mp="2mvno1rlxb3j5" />
            Add User
          </button>
        </div>
      </div>

      {/* Error */}
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700" data-id="element-631" data-mp="2hwu7trf94yfa">
          {error}
        </div>}

      {/* Filters */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-id="element-632" data-mp="22r6ohb6nx4am">
        <div className="grid grid-cols-2 gap-3" style={{
        maxWidth: 480
      }} data-id="element-633" data-mp="27q9rkq3310fy">
          <div data-id="element-634" data-mp="2cqfclneujop2">
            <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-635" data-mp="2cpw570mtoipa">Search</label>
            <input type="text" value={filterSearch} onChange={e => setFilterSearch(e.target.value)} placeholder="Email or name..." className="w-full px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-636" data-mp="2hpsar4b64i6y" />
          </div>
          <div data-id="element-637" data-mp="1yqecia6eolan">
            <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-638" data-mp="1yqwihyivedvr">Access Level</label>
            <select value={filterAccessLevel} onChange={e => setFilterAccessLevel(e.target.value)} className="w-full px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-639" data-mp="11biku5ra2le0">
              <option value="" data-id="element-640" data-mp="tb2u5tr7qisk">All levels</option>
              {ACCESS_LEVELS.map(level => <option key={level} value={level} data-id="element-641" data-mp="17zqpbyx5cqhz">{level}</option>)}
            </select>
          </div>
        </div>
        {(filterSearch || filterAccessLevel) && <button onClick={() => {
        setFilterSearch('');
        setFilterAccessLevel('');
      }} className="mt-2 text-xs text-indigo-600 hover:text-indigo-800 font-medium" data-id="element-642" data-mp="fa9xp3hcpdmt">
            Clear filters
          </button>}
      </div>

      {/* Add/Edit Form */}
      {showAddForm && <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3" data-id="element-643" data-mp="k9398qd2uukc">
          <h4 className="text-sm font-semibold text-slate-700" data-id="element-644" data-mp="20nmhcqbrtu8f">
            {editingUser ? 'Edit User' : 'Add New User'}
          </h4>
          <div className="grid grid-cols-3 gap-3" data-id="element-645" data-mp="1vo04ihzuug05">
            <div data-id="element-646" data-mp="238apqhx7b57z">
              <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-647" data-mp="237rhjsxcrv75">Email *</label>
              <input type="email" value={formData.email} onChange={e => setFormData(p => ({
            ...p,
            email: e.target.value
          }))} disabled={!!editingUser} className="w-full px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-slate-100 disabled:text-slate-500" placeholder="user@adobe.com" data-id="element-648" data-mp="287x2kq94ajg9" />
            </div>
            <div data-id="element-649" data-mp="2d705o55jefll">
              <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-650" data-mp="2d5faqefb8un7">Name</label>
              <input type="text" value={formData.name} onChange={e => setFormData(p => ({
            ...p,
            name: e.target.value
          }))} className="w-full px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Display name" data-id="element-651" data-mp="2h70l9t4dgwu8" />
            </div>
            <div data-id="element-652" data-mp="1h06zcz540c25">
              <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-653" data-mp="1gznepao2r9kf">Access Level *</label>
              <select value={formData.accessLevel} onChange={e => setFormData(p => ({
            ...p,
            accessLevel: e.target.value as AdobeImsAccessLevel
          }))} className="w-full px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-654" data-mp="1c25bm0wqnokc">
                {ACCESS_LEVELS.map(level => <option key={level} value={level} data-id="element-655" data-mp="1uyda8lv8x16o">{level}</option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1" data-id="element-656" data-mp="2nhailh3wk99r">
            <button onClick={() => void handleSave()} disabled={saving || !formData.email} className="px-4 py-1.5 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors" data-id="element-657" data-mp="2nivepb5v809q">
              {saving ? 'Saving...' : editingUser ? 'Update' : 'Create'}
            </button>
            <button onClick={() => {
          setShowAddForm(false);
          setEditingUser(null);
        }} className="px-4 py-1.5 rounded-md border border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors" data-id="element-658" data-mp="e71cxr7t1xf7">
              Cancel
            </button>
          </div>
        </div>}

      {/* Table */}
      {loading && users.length === 0 ? <div className="flex items-center justify-center py-12 text-slate-400 text-sm" data-id="element-659" data-mp="xx53ah9mrs0q">
          Loading users...
        </div> : <SortableTable columns={columns} data={filteredUsers} rowKey={row => row.email} defaultSort={{
      key: 'email',
      dir: 'asc'
    }} data-id="element-660" data-mp="16x9y7rxn0xy0" />}

      {/* Delete Confirmation */}
      {confirmDelete && <div className="fixed inset-0 z-60 flex items-center justify-center" style={{
      background: 'rgba(0,0,0,0.4)'
    }} data-id="element-661" data-mp="37zsn9hd2jb1y">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()} data-id="element-662" data-mp="32y3ek8dbj9h1">
            <h4 className="text-base font-semibold text-slate-900" data-id="element-663" data-mp="3cycdvv2lxb0n">Delete User</h4>
            <p className="mt-2 text-sm text-slate-600" data-id="element-664" data-mp="3cwj3q5ssc8nw">
              Are you sure you want to delete <strong data-id="element-665" data-mp="3qrtvvbfnafok">{confirmDelete}</strong>? This action cannot be undone.
            </p>
            <div className="mt-4 flex items-center gap-2 justify-end" data-id="element-666" data-mp="3mnvxx4223ax3">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-1.5 rounded-md border border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors" data-id="element-667" data-mp="4hhkd67p92z8">
                Cancel
              </button>
              <button onClick={() => void handleDelete(confirmDelete)} disabled={saving} className="px-4 py-1.5 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors" data-id="element-668" data-mp="21bm3lkpb8opa">
                {saving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>}

      {/* Tracker Add/Edit Modal */}
      {trackerModalUser && <div className="fixed inset-0 z-60 flex items-center justify-center" style={{
      background: 'rgba(0,0,0,0.4)'
    }} data-id="element-669" data-mp="3u7y2d105saog">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()} data-id="element-670" data-mp="31n7xnpnm863">
            <h4 className="text-base font-semibold text-slate-900" data-id="element-671" data-mp="3kasb9srjojo8">
              {trackerUserMap.has(trackerModalUser.email.toLowerCase()) ? 'Edit Tracker Access' : 'Add to Tracker'}
            </h4>
            <p className="mt-1 text-sm text-slate-500" data-id="element-672" data-mp="1ijf755xeift8">
              {trackerModalUser.email}
            </p>

            {trackerError && <div className="mt-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700" data-id="element-673" data-mp="b7vfbckvqzpq">
                {trackerError}
              </div>}

            <div className="mt-4" data-id="element-674" data-mp="6ava13vuu0zo">
              <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-675" data-mp="1hsbsig4u3d5">Tracker Access Level</label>
              <select value={trackerAccessLevel} onChange={e => setTrackerAccessLevel(e.target.value)} className="w-full px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-676" data-mp="3sov6vkz3ctsn">
                {TRACKER_ACCESS_LEVELS.map(level => <option key={level} value={level} data-id="element-677" data-mp="39ph8mqubwwwc">{level}</option>)}
              </select>
            </div>

            <div className="mt-5 flex items-center gap-2 justify-end" data-id="element-678" data-mp="1cxh29jslhkip">
              <button onClick={() => setTrackerModalUser(null)} className="px-4 py-1.5 rounded-md border border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors" data-id="element-679" data-mp="17y4emwtwg9f2">
                Cancel
              </button>
              <button onClick={() => void handleTrackerSave()} disabled={trackerSaving} className="px-4 py-1.5 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors" data-id="element-680" data-mp="1kedjk71mpsgz">
                {trackerSaving ? 'Saving...' : trackerUserMap.has(trackerModalUser.email.toLowerCase()) ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>}
    </div>;
}